// The items-list inputs of an extended open-ended question, ported from the shared coffee list
// view (ItemsListView#calcItemsListProps) for this family alone.
//
// The coffee view still serves matrix, heatmap, rapid-association, menu-test and the correlation
// finder, so only the branches oex actually reaches are here: an answer-variant row per variant,
// the trait breakdown a row expands into, and the sort the shared s<key> section carries. An
// extended open-ended question is never a pre-qualification question, so no screener marks or
// blanks a row.

import { encodeLabel, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { getFeature, getModelValue, resolveTrait } from '@aytm/app/stats/lib/stat-page'
import {
  isClickable,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType,
  processText
} from '@aytm/app/stats/page/components/lib/utils'
import { hasBase as answerHasBase } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import { calcValueCell } from '@aytm/app/stats/page/question-views/answer-variants/lib/table-utils'
import { answerSettled } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { colorIndexOf } from '@aytm/app/stats/page/question-views/components/lib/color-index'
import { calcTraitTable } from '@aytm/app/stats/page/question-views/components/lib/trait-table'
import { calcIgnoreAmount, calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const traitValuesOf = trait => {
  if (null == trait) return []

  return (trait.values || trait.answerVariants || []).map(value => ({
    id: (Object.hasOwn(value, 'id') && value.id) || (Object.hasOwn(value, 'position') && `a${value.position}`) || value,
    name:
      (Object.hasOwn(value, 'name') && encodeLabel(value.name.en || value.name)) ||
      (Object.hasOwn(value, 'position') && `A${value.position}`) ||
      value
  }))
}

// What the progress column says it is measuring, which depends on what the question counts.
// The extended open-end reports what its figures were taken against rather than naming a count:
// its rows are coded answers, so the respondents behind them are the base, not the tally.
const buildProgressTitle = ({ answerType, answer, hasBase, hideTotal, rType }) => {
  if ('amount' !== answerType) {
    return hideTotal ? '' : `<small>(N=${formatAmounts(answer, 'total', 0)})</small>`
  }

  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`
  if (hideTotal) return ''

  const total = 'cases' === rType ? 'total' : 'respTotal'

  return `<small>(based on ${formatAmounts(answer, total, 0)} responses)</small>`
}

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcTableProps = ({
  id,
  questionId,
  type,
  format,
  components,
  answer,
  answerVariants,
  answerType = 'amount',
  sortOrder = 'iasc',
  clickable = true,
  handlers = {}
} = {}) => {
  const variants = (answerVariants || []).filter(av => av)
  if (!variants.length) return { cols: [], rows: [] }

  const isRType = calcIsRType({ type })
  const rType = isRType ? getModelValue('rType') : 'cases'

  const traitName = getModelValue('trait')
  const trait = resolveTrait(traitName)
  const traitValues = traitValuesOf(trait)

  const isTraitType = calcIsTraitType({ format, type })
  const isLoading = 'loading' === answer
  const settled = answerSettled(answer)
  const hasBase = answerHasBase(answer)

  const hideTotal = true === format?.hideTotal
  const ignoreRefs = true === format?.ignoreRefs
  const ignoreTraits = true === format?.ignoreTraits
  const ignore = { amount: calcIgnoreAmount(format), percents: calcIgnorePercents(format) }
  const textAmount = true === format?.textAmount

  const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
    ? format.percentDecimals
    : getModelValue('percentDecimals')

  const commentsOn =
    (!components || undefined === components.comments || true === components.comments) &&
    'off' !== getFeature('individual_data')?.status?.oe

  const cols = [
    { type: 'title', title: 'Answers', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) },
    {
      type: 'progress',
      title: buildProgressTitle({ answerType, answer, hasBase, hideTotal, rType }),
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000)),
      format: value => {
        if (!Number.isFinite(value)) return value

        return textAmount ? value : formatAmounts(value)
      }
    }
  ]

  const rows = variants.map(variant => {
    const variantAnswer = answer?.[`a${variant.position}`] || {}

    const title = {
      title: processText(variant.text),
      colorIndex: trait && isTraitType && !ignoreTraits ? 0 : colorIndexOf(variant.position)
    }

    if (!ignoreRefs) title.label = `A${variant.position}`

    if (Object.hasOwn(variant, 'image')) title.image = variant.image

    if (Object.hasOwn(variant, 'skipsTo')) {
      title.skips_to = variant.skipsTo
      title.onSkipsToClick = handlers.onSkipsToClick
    }

    if (commentsOn && Object.hasOwn(variantAnswer, 'comments')) {
      title.comments = variantAnswer.comments
      title.onCommentsClick = handlers.onCommentsClick
    }

    if (isClickable(format, clickable)) title.onLabelClick = handlers.onLabelClick

    const row = {
      position: variant.position,
      cells: [title, calcValueCell({ variant, variantAnswer, answerType, rType, isLoading, settled, ignore })]
    }

    // a trait breakdown is a table of its own the row expands into
    if (trait && isTraitType && undefined !== variantAnswer[traitName]) {
      row.table = calcTraitTable({
        id,
        trait,
        traitName,
        traitValues,
        traitAnswer: variantAnswer[traitName],
        answerPosition: variant.position
      })
    }

    return row
  })

  return { id, questionId, cols: applySortedColumn(cols, sortOrder), rows, onSort: handlers.onSort }
}
